import React from "react";
import Tooltip from "@mui/material/Tooltip";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import clsx from "clsx";
import { getNotificationVariantStyle, unreadStyles } from "./styles/notificationStyles";
import { formatDate } from "@utils/formatDate";
// import { useNavigate } from "react-router-dom";
// import { BROWSER_ROUTES } from "@constants/browser.routes";
import type { NotificationItemProps } from "./types/types";
import { useTranslation } from "react-i18next";

const NotificationItem: React.FC<NotificationItemProps> = ({ notification: n, markRead, setOpen }) => {
	// const navigate = useNavigate();
	const { t } = useTranslation();
	const styles = getNotificationVariantStyle(n.variant);

	// Debug log to see if isRead is changing
	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		// Mark as read if not already read
		if (!n.isRead) {
			markRead(n.uuid);
			// navigate(BROWSER_ROUTES.APPLICATIONS);
			setOpen(true);
		}
	};

	return (
		<div
			className={clsx(
				"relative rounded-md shadow border p-4 transition-all cursor-pointer transform group",
				styles.bg,
				styles.border,
				n.isRead
					? "opacity-60 hover:shadow-md hover:scale-[1.02]"
					: clsx(styles.unreadContainer, styles.unreadHover, "hover:scale-[1.02]"),
			)}
			onClick={handleClick}>
			{/* Top row: title and New badge */}
			<div className="flex items-center mb-1">
				{/* Unread indicator dot - positioned relative to title */}
				{!n.isRead && <div className={clsx("w-2 h-2 rounded-full mr-2", styles.unreadIndicator)} />}
				<Tooltip
					title={n.title.length > 50 ? n.title : ""}
					arrow
					placement="top"
					disableHoverListener={n.title.length <= 50}>
					<span
						className={clsx(
							"font-semibold truncate text-base text-ellipsis flex-1 transition-colors duration-200",
							styles.title,
							!n.isRead && unreadStyles.title,
							styles.titleHover,
						)}>
						{n.title}
					</span>
				</Tooltip>
				{!n.isRead && (
					<span className={clsx("ml-2 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0", styles.unreadBadge)}>
						{t("psdaCommon.notifications.newNotifications")}
					</span>
				)}
			</div>

			{/* Description/message with tooltip */}
			<Tooltip
				title={n.message.length > 100 ? n.message : ""}
				arrow
				placement="top"
				disableHoverListener={n.message.length <= 100}>
				<div className={clsx("text-gray-600 text-sm mb-1 line-clamp-2", !n.isRead && "ml-4")}>{n.message}</div>
			</Tooltip>

			{/* Bottom row: date only */}
			<div className={clsx("flex items-center justify-end mt-2", !n.isRead && "ml-4")}>
				<span className="flex items-center text-xs text-gray-500 min-w-fit">
					<AccessTimeIcon className="w-3 h-3 mr-1" fontSize="inherit" />
					{formatDate(n.createdAt)}
				</span>
			</div>
		</div>
	);
};

export default NotificationItem;
